// src/pages/PublicEventDetailPage.js
// Public detail view for a single event. Embeddable in Wix via iframe.
// No login required. Phase 14: adds inline registration form.

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

const api = axios.create({ baseURL: '/api/public' });

// Resolve which price the public registrant sees. Priority:
//   generic_price (if shown) → non_member_price (if shown) → member_price (if shown).
// Defaults preserve existing behavior (non_member shown by default).
function getPublicPrice(tt) {
  if (!tt) return 0;
  if (tt.show_generic_price && tt.generic_price != null) return parseFloat(tt.generic_price);
  if (tt.show_non_member_price && tt.non_member_price != null) return parseFloat(tt.non_member_price);
  if (tt.show_member_price && tt.member_price != null) return parseFloat(tt.member_price);
  return 0;
}

export default function PublicEventDetailPage() {
  const { id } = useParams();
  const initialPromo = (new URLSearchParams(window.location.search).get('promo') || '').trim();
  const [event, setEvent] = useState(null);
  const [fieldConfig, setFieldConfig] = useState({ requireBusiness: true, requirePhone: false, requireSponsorBusiness: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preselectedTT, setPreselectedTT] = useState(null);

  const selectAndScroll = (ttId) => {
    setPreselectedTT(ttId);
    setTimeout(() => {
      const target = document.querySelector('[data-register-form]');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  useEffect(() => {
    api.get(`/events/${id}`)
      .then(res => setEvent(res.data))
      .catch(err => {
        if (err.response?.status === 404) {
          setError('This event is no longer available.');
        } else {
          setError('Unable to load event. Please try again later.');
        }
      })
      .finally(() => setLoading(false));

    // Fetch field config — non-blocking, falls back to defaults if it fails
    api.get('/registration-form-fields')
      .then(res => setFieldConfig({
        requireBusiness: !!res.data.requireBusiness,
        requirePhone:    !!res.data.requirePhone,
        requireSponsorBusiness: !!res.data.requireSponsorBusiness,
      }))
      .catch(() => { /* keep defaults */ });
  }, [id]);

  if (loading) {
    return (
      <div style={pageStyle}><div style={statusStyle}>Loading…</div></div>
    );
  }

  if (error || !event) {
    return (
      <div style={pageStyle}>
        <div style={containerStyle}>
          <BackToEvents />
          <div style={{ ...statusStyle, color: '#b00' }}>{error || 'Not found.'}</div>
        </div>
      </div>
    );
  }

  const dateLabel = formatDate(event.event_date);
  const timeLabel = formatTime(event.start_time, event.end_time);
  const hasTickets = event.ticketTypes && event.ticketTypes.length > 0;
  const availableTickets = (event.ticketTypes || []).filter(tt =>
    tt.quantity_available == null || tt.quantity_sold < tt.quantity_available
  );
  const allSoldOut = hasTickets && availableTickets.length === 0;

  return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        <BackToEvents />

        <article style={cardStyle}>
          {event.has_flyer && (
            <PublicFlyer eventId={event.id} eventName={event.name} />
          )}

          <div style={bodyStyle}>
            <div style={dateBadgeStyle}>{dateLabel}</div>
            <h1 style={titleStyle}>{event.name}</h1>

            <div style={metaGridStyle}>
              {timeLabel && (
                <div style={metaItemStyle}>
                  <strong>Time:</strong> {timeLabel}
                </div>
              )}
              {event.venue && (
                <div style={metaItemStyle}>
                  <strong>Venue:</strong> {event.venue}
                </div>
              )}
              {event.location_address && (
                <div style={metaItemStyle}>
                  <strong>Address:</strong> {event.location_address}
                </div>
              )}
            </div>

            {/* [marker: website-feed v1] Add to Calendar now appears only after registering (confirmation page and email) */}

            {event.description && (
              <div style={descriptionStyle}>
                {event.description.split('\n').map((line, i) => (
                  <p key={i} style={{ margin: '0 0 12px' }}>{line}</p>
                ))}
              </div>
            )}

            {hasTickets && (
              <>
                {(() => {
                  const sponsorships = event.ticketTypes.filter(tt => tt.category === 'sponsorship');
                  const tickets      = event.ticketTypes.filter(tt => (tt.category || 'ticket') === 'ticket');
                  return (
                    <>
                      {tickets.length > 0 && (
                        <section style={ticketsSectionStyle}>
                          {event.registration_message && (
                            <div style={eventMessageStyle}>{event.registration_message}</div>
                          )}
                          <h2 style={sectionTitleStyle}>Tickets</h2>
                          <div style={ticketsListStyle}>
                            {tickets.map(tt => (
                              <TicketTypeRow key={tt.id} tt={tt} />
                            ))}
                          </div>
                        </section>
                      )}

                      {sponsorships.length > 0 && (
                        <section style={sponsorshipsSectionStyle}>
                          <h2 style={sectionTitleStyle}>Sponsorship Opportunities</h2>
                          <div style={{ fontSize: 14, color: '#475569', marginBottom: 16, marginTop: -8 }}>
                            Support this event with one of the sponsorship levels below.
                          </div>
                          <div style={sponsorshipsListStyle}>
                            {sponsorships.map(tt => (
                              <SponsorshipCard
                                key={tt.id}
                                tt={tt}
                                onSelect={selectAndScroll}
                              />
                            ))}
                          </div>
                        </section>
                      )}
                    </>
                  );
                })()}
              </>
            )}

            <section style={registerSectionStyle} data-register-form>
              <h2 style={sectionTitleStyle}>Register</h2>
              {!hasTickets ? (
                <p style={registerTextStyle}>
                  Registration is not yet available for this event. Check back soon, or email{' '}
                  <a href="mailto:info@njjba.com" style={linkStyle}>info@njjba.com</a> with questions.
                </p>
              ) : allSoldOut ? (
                <p style={{ ...registerTextStyle, color: '#991b1b', fontWeight: 600 }}>
                  This event is sold out. Email{' '}
                  <a href="mailto:info@njjba.com" style={linkStyle}>info@njjba.com</a>{' '}
                  to join the waitlist.
                </p>
              ) : (
                <RegistrationForm
                  event={event}
                  availableTickets={availableTickets}
                  fieldConfig={fieldConfig}
                  preselectedTicketTypeId={preselectedTT}
                  initialPromo={initialPromo}
                />
              )}
            </section>

            <PublicSponsorSection event={event} fieldConfig={fieldConfig} />
          </div>
        </article>

        <footer style={footerStyle}>
          New Jersey Jewish Business Alliance · <a href="mailto:info@njjba.com" style={linkStyle}>info@njjba.com</a>
        </footer>
      </div>
    </div>
  );
}

// ─── AddToCalendarButton ────────────────────────────────────────────────────
// Adds the event to Google / Outlook.com / Office 365 / Apple. Event times are
// Eastern, converted to the correct UTC instant (DST-aware) so the entry is
// right in any timezone. Apple / any other app uses the server-built .ics.
const CAL_EVENT_TZ = 'America/New_York';
const CAL_DEFAULT_DURATION_MIN = 120; // 2 hours when the event has no end time

function calYMD(ed) {
  if (!ed) return null;
  const s = typeof ed === 'string' ? ed : new Date(ed).toISOString();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return { y: +m[1], mo: +m[2], d: +m[3] };
  const dt = new Date(ed);
  return { y: dt.getUTCFullYear(), mo: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}
function calEtWallToUTC(y, mo, d, hh, mm) {
  const guess = Date.UTC(y, mo - 1, d, hh, mm, 0);
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: CAL_EVENT_TZ, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const p = {};
  dtf.formatToParts(new Date(guess)).forEach(x => { p[x.type] = x.value; });
  const asIfUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return new Date(guess - (asIfUTC - guess));
}
function calPad2(n) { return String(n).padStart(2, '0'); }
function calCompactUTC(date) {
  return date.getUTCFullYear() + calPad2(date.getUTCMonth() + 1) + calPad2(date.getUTCDate())
    + 'T' + calPad2(date.getUTCHours()) + calPad2(date.getUTCMinutes()) + calPad2(date.getUTCSeconds()) + 'Z';
}
function calISOUTC(date) {
  return date.getUTCFullYear() + '-' + calPad2(date.getUTCMonth() + 1) + '-' + calPad2(date.getUTCDate())
    + 'T' + calPad2(date.getUTCHours()) + ':' + calPad2(date.getUTCMinutes()) + ':' + calPad2(date.getUTCSeconds()) + 'Z';
}

function AddToCalendarButton({ event }) {
  const [open, setOpen] = useState(false);
  const wrapRef = React.useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const ymd = calYMD(event.event_date);
  if (!ymd) return null;

  const hasTime = !!event.start_time;
  let startUTC = null, endUTC = null;
  if (hasTime) {
    const [sh, sm] = String(event.start_time).split(':').map(n => parseInt(n, 10));
    startUTC = calEtWallToUTC(ymd.y, ymd.mo, ymd.d, sh, sm || 0);
    if (event.end_time) {
      const [eh, em] = String(event.end_time).split(':').map(n => parseInt(n, 10));
      endUTC = calEtWallToUTC(ymd.y, ymd.mo, ymd.d, eh, em || 0);
      if (endUTC <= startUTC) endUTC = new Date(startUTC.getTime() + CAL_DEFAULT_DURATION_MIN * 60000);
    } else {
      endUTC = new Date(startUTC.getTime() + CAL_DEFAULT_DURATION_MIN * 60000);
    }
  }

  const title = event.name || 'NJJBA Event';
  const location = [event.venue, event.location_address].filter(Boolean).join(', ');
  const eventUrl = `${window.location.origin}/embed/events/${event.id}`;
  const details = [event.description, `Details & registration: ${eventUrl}`].filter(Boolean).join('\n\n');

  const googleUrl = (() => {
    const p = new URLSearchParams({ action: 'TEMPLATE', text: title });
    if (hasTime) {
      p.set('dates', `${calCompactUTC(startUTC)}/${calCompactUTC(endUTC)}`);
    } else {
      const d1 = `${ymd.y}${calPad2(ymd.mo)}${calPad2(ymd.d)}`;
      const next = new Date(Date.UTC(ymd.y, ymd.mo - 1, ymd.d) + 86400000);
      const d2 = `${next.getUTCFullYear()}${calPad2(next.getUTCMonth() + 1)}${calPad2(next.getUTCDate())}`;
      p.set('dates', `${d1}/${d2}`);
    }
    if (details) p.set('details', details);
    if (location) p.set('location', location);
    return `https://calendar.google.com/calendar/render?${p.toString()}`;
  })();

  const outlookUrl = (host) => {
    const p = new URLSearchParams({ path: '/calendar/action/compose', rru: 'addevent', subject: title });
    if (hasTime) {
      p.set('startdt', calISOUTC(startUTC));
      p.set('enddt', calISOUTC(endUTC));
    } else {
      p.set('allday', 'true');
      p.set('startdt', `${ymd.y}-${calPad2(ymd.mo)}-${calPad2(ymd.d)}`);
    }
    if (details) p.set('body', details);
    if (location) p.set('location', location);
    return `https://${host}/calendar/0/deeplink/compose?${p.toString()}`;
  };

  const icsUrl = `/api/public/events/${event.id}/calendar.ics`;
  const openUrl = (url) => { window.open(url, '_blank', 'noopener,noreferrer'); setOpen(false); };

  const items = [
    { label: 'Google Calendar', onClick: () => openUrl(googleUrl) },
    { label: 'Outlook.com', onClick: () => openUrl(outlookUrl('outlook.live.com')) },
    { label: 'Office 365', onClick: () => openUrl(outlookUrl('outlook.office.com')) },
    { label: 'Apple / Download (.ics)', onClick: () => { window.location.href = icsUrl; setOpen(false); } },
  ];

  return (
    <div ref={wrapRef} style={calWrapStyle}>
      <button type="button" onClick={() => setOpen(o => !o)} style={calButtonStyle} aria-haspopup="true" aria-expanded={open}>
        <span style={{ marginRight: 8 }}>📅</span> Add to Calendar <span style={{ marginLeft: 8, fontSize: 11 }}>▾</span>
      </button>
      {open && (
        <div style={calMenuStyle} role="menu">
          {items.map((it, i) => (
            <button key={i} type="button" onClick={it.onClick} style={calMenuItemStyle} role="menuitem"
                    onMouseDown={e => e.preventDefault()}>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const calWrapStyle = { position: 'relative', display: 'inline-block', marginBottom: 20 };
const calButtonStyle = {
  display: 'inline-flex', alignItems: 'center',
  background: '#fff', color: '#1a3a5c',
  border: '2px solid #1a3a5c', borderRadius: 6,
  padding: '9px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
};
const calMenuStyle = {
  position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 50,
  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
  boxShadow: '0 8px 24px rgba(0,0,0,0.14)', overflow: 'hidden', minWidth: 230,
};
const calMenuItemStyle = {
  display: 'block', width: '100%', textAlign: 'left',
  padding: '11px 16px', background: '#fff', border: 'none',
  borderBottom: '1px solid #f1f5f9', fontSize: 14, color: '#1f2937', cursor: 'pointer',
};

// ─── RegistrationForm ──────────────────────────────────────────────────────
const MAX_TICKETS_PER_ORDER = 10;

function blankAttendee() {
  return { firstName: '', lastName: '', email: '', businessName: '', phone: '' };
}

function RegistrationForm({ event, availableTickets, fieldConfig, preselectedTicketTypeId, initialPromo }) {
  const [ticketTypeId, setTicketTypeId] = useState(availableTickets[0]?.id || '');
  const [attendees, setAttendees] = useState([blankAttendee()]);
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [registeredCount, setRegisteredCount] = useState(1);
  const [error, setError] = useState(null);

  // Promo code state
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoText, setPromoText] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // null | { code, discountAmount(per ticket), finalPrice(per ticket), discountType, discountValue }
  const [promoError, setPromoError] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);
  const [didAutoPromo, setDidAutoPromo] = useState(false);

  const requireBusiness = !!fieldConfig?.requireBusiness;
  const requirePhone    = !!fieldConfig?.requirePhone;

  const qty = attendees.length;
  const selectedTicket = availableTickets.find(tt => String(tt.id) === String(ticketTypeId));
  const basePrice = getPublicPrice(selectedTicket);            // per ticket
  const perTicketDiscount = appliedPromo ? appliedPromo.discountAmount : 0;
  const perTicketFinal = appliedPromo ? appliedPromo.finalPrice : basePrice;
  const orderSubtotal = (basePrice || 0) * qty;
  const orderDiscount = perTicketDiscount * qty;
  const orderTotal = Math.max(0, (perTicketFinal || 0) * qty);

  // Re-validate a promo against the currently-selected ticket type. The server
  // returns a PER-TICKET discount; we multiply by qty for the order display.
  const revalidatePromo = async (code, ttId) => {
    try {
      const res = await api.post(`/events/${event.id}/validate-promo`, {
        code,
        ticketTypeId: ttId || ticketTypeId,
      });
      if (res.data?.valid) {
        setAppliedPromo({
          code: res.data.code,
          discountAmount: res.data.discountAmount,
          finalPrice: res.data.finalPrice,
          discountType: res.data.discountType,
          discountValue: res.data.discountValue,
        });
      } else {
        setAppliedPromo(null);
        setPromoError(res.data?.error || 'Code is no longer valid.');
      }
    } catch (_) { /* keep current state */ }
  };

  // When a sponsorship card (or any external trigger) selects a ticket type,
  // sync it here and re-validate any applied promo.
  useEffect(() => {
    if (preselectedTicketTypeId != null && String(preselectedTicketTypeId) !== String(ticketTypeId)) {
      setTicketTypeId(String(preselectedTicketTypeId));
      if (appliedPromo) revalidatePromo(appliedPromo.code, preselectedTicketTypeId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedTicketTypeId]);

  // Auto-apply a promo code passed in the URL (?promo=CODE). Runs once.
  useEffect(() => {
    if (didAutoPromo || !initialPromo || !ticketTypeId) return;
    setDidAutoPromo(true);
    (async () => {
      try {
        const res = await api.post(`/events/${event.id}/validate-promo`, {
          code: initialPromo,
          ticketTypeId,
        });
        if (res.data?.valid) {
          setAppliedPromo({
            code: res.data.code,
            discountAmount: res.data.discountAmount,
            finalPrice: res.data.finalPrice,
            discountType: res.data.discountType,
            discountValue: res.data.discountValue,
          });
        } else {
          setPromoText(initialPromo);
          setShowPromoInput(true);
          setPromoError(res.data?.error || 'That promo code is not valid for this event.');
        }
      } catch (_) {
        setPromoText(initialPromo);
        setShowPromoInput(true);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPromo, ticketTypeId, didAutoPromo]);

  const changeTicketType = (v) => {
    setTicketTypeId(v);
    if (appliedPromo) revalidatePromo(appliedPromo.code, v);
  };

  const changeQty = (n) => {
    const next = Math.max(1, Math.min(MAX_TICKETS_PER_ORDER, n));
    setAttendees(prev => {
      const copy = prev.slice(0, next);
      while (copy.length < next) copy.push(blankAttendee());
      return copy;
    });
  };

  const setAttendeeField = (idx, key, value) => {
    setAttendees(prev => prev.map((a, i) => (i === idx ? { ...a, [key]: value } : a)));
  };

  const handleApplyPromo = async () => {
    setPromoError(null);
    const code = promoText.trim().toUpperCase();
    if (!code) {
      setPromoError('Please enter a code.');
      return;
    }
    setPromoChecking(true);
    try {
      const res = await api.post(`/events/${event.id}/validate-promo`, {
        code,
        ticketTypeId,
      });
      if (res.data?.valid) {
        setAppliedPromo({
          code: res.data.code,
          discountAmount: res.data.discountAmount,
          finalPrice: res.data.finalPrice,
          discountType: res.data.discountType,
          discountValue: res.data.discountValue,
        });
        setPromoText('');
        setShowPromoInput(false);
      } else {
        setPromoError(res.data?.error || 'Invalid code.');
      }
    } catch (err) {
      setPromoError('Could not validate code. Please try again.');
    } finally {
      setPromoChecking(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    for (let i = 0; i < attendees.length; i++) {
      const a = attendees[i];
      const label = attendees.length > 1 ? `Attendee ${i + 1}: ` : '';
      if (!a.firstName.trim() || !a.lastName.trim()) {
        setError(`${label}Please enter first and last name.`);
        return;
      }
      if (!a.email.trim() || !a.email.includes('@')) {
        setError(`${label}Please enter a valid email address.`);
        return;
      }
      if (requireBusiness && !a.businessName.trim()) {
        setError(`${label}Please enter a business or organization name.`);
        return;
      }
      if (requirePhone && !a.phone.trim()) {
        setError(`${label}Please enter a phone number.`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        ticketTypeId,
        attendees: attendees.map(a => ({
          firstName: a.firstName.trim(),
          lastName: a.lastName.trim(),
          email: a.email.trim(),
          businessName: a.businessName.trim(),
          phone: a.phone.trim(),
        })),
      };
      if (appliedPromo) payload.promoCode = appliedPromo.code;
      const res = await api.post(`/events/${event.id}/register`, payload);

      if (res.data?.free) {
        setRegisteredCount(res.data.ticketCount || attendees.length);
        setRegistered(true);
        window.scrollTo && window.scrollTo(0, 0);
        return;
      }
      if (res.data?.paymentUrl) {
        // [marker: confirm-polish v1] remember the order so the thank-you page can show it after payment
        try {
          localStorage.setItem('njjba-last-order', JSON.stringify({
            orderRef: res.data.orderRef, eventId: event.id, at: Date.now(), paymentUrl: res.data.paymentUrl,
            email: attendees[0].email.trim(), names: attendees.map(a => `${a.firstName.trim()} ${a.lastName.trim()}`.trim()),
          }));
        } catch (_) { /* private browsing */ }
        setRedirecting(true);
        setTimeout(() => {
          window.location.href = res.data.paymentUrl;
        }, 800);
      } else {
        setError('Registration succeeded but no payment link returned. Please contact NJJBA.');
        setSubmitting(false);
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Registration failed. Please try again.';
      setError(msg);
      setSubmitting(false);
    }
  };

  if (registered) { // [marker: confirm-polish v1]
    return (
      <RegistrationConfirmed event={event} status="paid" free count={registeredCount}
        email={attendees[0]?.email} names={attendees.map(a => `${a.firstName} ${a.lastName}`.trim())} />
    );
  }

  if (redirecting) {
    return (
      <div style={{ padding: '32px 16px', textAlign: 'center' }}>
        <div style={{ fontSize: 17, fontWeight: 600, color: '#1a3a5c', marginBottom: 8 }}>
          Redirecting to secure payment…
        </div>
        <div style={{ fontSize: 14, color: '#6b7280' }}>
          Your {qty > 1 ? `${qty} reservations are` : 'reservation is'} held while you complete payment. Please don't close this window.
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {availableTickets.length > 1 && (
        <div style={formGroupStyle}>
          <label style={labelStyle}>Ticket Type</label>
          <select
            style={inputStyle}
            value={ticketTypeId}
            onChange={e => changeTicketType(e.target.value)}
            required
          >
            {availableTickets.map(tt => (
              <option key={tt.id} value={tt.id}>
                {tt.name} — ${getPublicPrice(tt).toFixed(2)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ── Quantity selector ─────────────────────────────────────────────── */}
      <div style={formGroupStyle}>
        <label style={labelStyle}>Number of Tickets</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            type="button"
            onClick={() => changeQty(qty - 1)}
            disabled={qty <= 1}
            style={{ ...qtyBtnStyle, opacity: qty <= 1 ? 0.4 : 1, cursor: qty <= 1 ? 'not-allowed' : 'pointer' }}
            aria-label="Remove a ticket"
          >
            −
          </button>
          <span style={{ fontSize: 20, fontWeight: 700, color: '#1a3a5c', minWidth: 28, textAlign: 'center' }}>
            {qty}
          </span>
          <button
            type="button"
            onClick={() => changeQty(qty + 1)}
            disabled={qty >= MAX_TICKETS_PER_ORDER}
            style={{ ...qtyBtnStyle, opacity: qty >= MAX_TICKETS_PER_ORDER ? 0.4 : 1, cursor: qty >= MAX_TICKETS_PER_ORDER ? 'not-allowed' : 'pointer' }}
            aria-label="Add a ticket"
          >
            +
          </button>
          <span style={{ fontSize: 13, color: '#6b7280', marginLeft: 4 }}>
            (up to {MAX_TICKETS_PER_ORDER})
          </span>
        </div>
        <div style={hintStyle}>Enter details for each attendee below. Everyone gets their own ticket and confirmation email.</div>
      </div>

      {/* ── Per-attendee blocks ───────────────────────────────────────────── */}
      {attendees.map((a, idx) => (
        <div key={idx} style={qty > 1 ? attendeeCardStyle : undefined}>
          {qty > 1 && (
            <div style={attendeeHeadingStyle}>Attendee {idx + 1}</div>
          )}

          <div style={formRowStyle}>
            <div style={{ ...formGroupStyle, flex: 1 }}>
              <label style={labelStyle}>First Name *</label>
              <input
                style={inputStyle}
                type="text"
                value={a.firstName}
                onChange={e => setAttendeeField(idx, 'firstName', e.target.value)}
                required
                autoComplete="off"
              />
            </div>
            <div style={{ ...formGroupStyle, flex: 1 }}>
              <label style={labelStyle}>Last Name *</label>
              <input
                style={inputStyle}
                type="text"
                value={a.lastName}
                onChange={e => setAttendeeField(idx, 'lastName', e.target.value)}
                required
                autoComplete="off"
              />
            </div>
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Email *</label>
            <input
              style={inputStyle}
              type="email"
              value={a.email}
              onChange={e => setAttendeeField(idx, 'email', e.target.value)}
              required
              autoComplete="off"
              placeholder="you@example.com"
            />
            <div style={hintStyle}>This attendee's ticket confirmation will be sent here.</div>
          </div>

          <div style={formRowStyle}>
            <div style={{ ...formGroupStyle, flex: 1 }}>
              <label style={labelStyle}>Business / Organization {requireBusiness && '*'}</label>
              <input
                style={inputStyle}
                type="text"
                value={a.businessName}
                onChange={e => setAttendeeField(idx, 'businessName', e.target.value)}
                autoComplete="off"
                placeholder={requireBusiness ? '' : 'Optional'}
                required={requireBusiness}
              />
            </div>
            <div style={{ ...formGroupStyle, flex: 1 }}>
              <label style={labelStyle}>Phone {requirePhone && '*'}</label>
              <input
                style={inputStyle}
                type="tel"
                value={a.phone}
                onChange={e => setAttendeeField(idx, 'phone', e.target.value)}
                autoComplete="off"
                placeholder={requirePhone ? '' : 'Optional'}
                required={requirePhone}
              />
            </div>
          </div>
        </div>
      ))}

      {/* ── Promo code section (applies to every ticket) ──────────────────── */}
      <div style={{ marginTop: 16, marginBottom: 8 }}>
        {appliedPromo ? (
          <div style={appliedPromoStyle}>
            <div>
              <span style={{ fontSize: 13, color: '#15803d', fontWeight: 600 }}>
                ✓ Code <strong>{appliedPromo.code}</strong> applied to all {qty} ticket{qty > 1 ? 's' : ''}
              </span>
              <div style={{ fontSize: 12, color: '#15803d', marginTop: 2 }}>
                Saving ${orderDiscount.toFixed(2)}
                {appliedPromo.discountType === 'percentage'
                  ? ` (${appliedPromo.discountValue}% off each)`
                  : ` ($${appliedPromo.discountAmount.toFixed(2)} off each)`}
              </div>
            </div>
            <button type="button" onClick={handleRemovePromo} style={removePromoBtn}>×</button>
          </div>
        ) : showPromoInput ? (
          <div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={promoText}
                onChange={e => setPromoText(e.target.value)}
                placeholder="Enter code"
                style={{ ...inputStyle, flex: 1 }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleApplyPromo(); } }}
              />
              <button
                type="button"
                onClick={handleApplyPromo}
                disabled={promoChecking}
                style={{ ...ctaButtonStyle, padding: '10px 18px', opacity: promoChecking ? 0.6 : 1 }}
              >
                {promoChecking ? 'Checking…' : 'Apply'}
              </button>
            </div>
            {promoError && (
              <div style={{ fontSize: 12, color: '#991b1b', marginTop: 6 }}>{promoError}</div>
            )}
            <button
              type="button"
              onClick={() => { setShowPromoInput(false); setPromoError(null); setPromoText(''); }}
              style={promoLinkBtn}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowPromoInput(true)}
            style={promoLinkBtn}
          >
            Have a promo code?
          </button>
        )}
      </div>

      {/* ── Order summary ─────────────────────────────────────────────────── */}
      <div style={summaryBoxStyle}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--gray-600, #6b7280)' }}>
            <span>{qty} × ${(basePrice || 0).toFixed(2)}</span>
            <span>${orderSubtotal.toFixed(2)}</span>
          </div>
          {appliedPromo && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#15803d', marginTop: 4 }}>
              <span>Discount ({appliedPromo.code})</span>
              <span>−${orderDiscount.toFixed(2)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTop: '1px solid #e5e7eb' }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>Total</span>
            <span style={{ fontSize: 22, fontWeight: 700, color: '#1a3a5c' }}>${orderTotal.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {error && (
        <div style={errorBoxStyle}>{error}</div>
      )}

      <button type="submit" disabled={submitting} style={{ ...ctaButtonStyle, ...submitButtonStyle, opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}>
        {submitting ? 'Processing…' : (orderTotal === 0 ? 'Complete Registration →' : 'Continue to Payment →')}
      </button>

      <div style={disclaimerStyle}>
        NJJBA members may be entitled to a discounted rate. If you are a current paid NJJBA member,
        please contact <a href="mailto:info@njjba.com" style={linkStyle}>info@njjba.com</a> before registering
        for member pricing. Payment is processed securely by Cardknox.
      </div>
    </form>
  );
}

// ─── PublicFlyer ────────────────────────────────────────────────────────────
function PublicFlyer({ eventId, eventName }) {
  const [failed, setFailed] = useState(false);
  const url = `/api/public/events/${eventId}/flyer`;
  if (failed) {
    return (
      <div style={{ ...flyerWrapStyle, padding: '32px 16px' }}>
        <a href={url} target="_blank" rel="noreferrer" style={ctaButtonStyle}>
          View Flyer (PDF)
        </a>
      </div>
    );
  }
  return (
    <div style={flyerWrapStyle}>
      <img
        src={url}
        alt={`Flyer for ${eventName}`}
        style={flyerImgStyle}
        onError={() => setFailed(true)}
      />
    </div>
  );
}

// ─── TicketTypeRow ──────────────────────────────────────────────────────────
function TicketTypeRow({ tt }) {
  const prices = [];
  if (tt.show_member_price && tt.member_price != null) {
    prices.push({ label: 'Member', value: parseFloat(tt.member_price) });
  }
  if (tt.show_non_member_price && tt.non_member_price != null) {
    prices.push({ label: 'Non-Member', value: parseFloat(tt.non_member_price) });
  }
  if (tt.show_generic_price && tt.generic_price != null) {
    prices.push({ label: 'Ticket', value: parseFloat(tt.generic_price) });
  }
  const soldOut    = tt.quantity_available != null && tt.quantity_sold >= tt.quantity_available;
  const showLabels = prices.length > 1;

  return (
    <div style={ticketRowStyle}>
      <div style={{ flex: 1 }}>
        <div style={ticketNameStyle}>{tt.name}</div>
        {tt.description && (
          <div style={ticketDescStyle}>{tt.description}</div>
        )}
        {soldOut && <div style={soldOutBadgeStyle}>Sold Out</div>}
      </div>
      <div style={pricesStyle}>
        {prices.length === 0 ? (
          <div style={{ color: '#6b7280', fontSize: 14 }}>—</div>
        ) : prices.map((p, i) => (
          <div key={i} style={priceItemStyle}>
            {showLabels && <div style={priceLabelStyle}>{p.label}</div>}
            <div style={priceValueStyle}>${p.value.toFixed(2)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SponsorshipCard ────────────────────────────────────────────────────────
function SponsorshipCard({ tt, onSelect }) {
  const [showDetail, setShowDetail] = useState(false);
  const price = (tt.show_generic_price && tt.generic_price != null) ? parseFloat(tt.generic_price)
              : (tt.show_non_member_price && tt.non_member_price != null) ? parseFloat(tt.non_member_price)
              : (tt.show_member_price && tt.member_price != null) ? parseFloat(tt.member_price)
              : 0;
  const soldOut = tt.quantity_available != null && tt.quantity_sold >= tt.quantity_available;

  return (
    <div style={sponsorshipCardStyle}>
      <div style={{ flex: 1, marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>{tt.name}</div>
      </div>
      {soldOut ? (
        <div style={{ ...sponsorshipButtonStyle, background: '#94a3b8', cursor: 'not-allowed' }}>
          Sold Out
        </div>
      ) : (
        <button style={sponsorshipLearnMoreStyle} onClick={() => setShowDetail(true)}>
          Click to learn more →
        </button>
      )}
      {showDetail && (
        <SponsorshipDetailModal
          tt={tt}
          price={price}
          soldOut={soldOut}
          onClose={() => setShowDetail(false)}
          onSponsor={() => { setShowDetail(false); onSelect && onSelect(tt.id); }}
        />
      )}
    </div>
  );
}

// #13 — "Click to learn more" detail screen: shows the price + benefits for a
// sponsorship level (kept off the main ticket page per request).
function SponsorshipDetailModal({ tt, price, soldOut, onClose, onSponsor }) {
  return (
    <div style={sponsorModalOverlayStyle} onClick={onClose}>
      <div style={sponsorModalStyle} onClick={e => e.stopPropagation()}>
        <button style={sponsorModalCloseStyle} onClick={onClose} aria-label="Close">×</button>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 6, paddingRight: 28 }}>{tt.name}</div>
        <div style={{ fontSize: 30, fontWeight: 800, color: '#1e3a5f', marginBottom: 18 }}>
          ${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </div>
        {tt.description ? (
          <div style={{ marginBottom: 22 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>Benefits</div>
            <div style={{ fontSize: 15, color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{tt.description}</div>
          </div>
        ) : (
          <div style={{ fontSize: 14, color: '#64748b', marginBottom: 22 }}>
            Contact us at <a href="mailto:info@njjba.com" style={linkStyle}>info@njjba.com</a> for full details on this sponsorship level.
          </div>
        )}
        {soldOut ? (
          <div style={{ ...sponsorshipButtonStyle, background: '#94a3b8', cursor: 'not-allowed' }}>Sold Out</div>
        ) : (
          <button style={sponsorshipButtonStyle} onClick={onSponsor}>Sponsor This Level →</button>
        )}
      </div>
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────
function PublicSponsorSection({ event, fieldConfig }) {
  const tiers = event.sponsorshipTiers || [];
  const requireBusiness = !!fieldConfig?.requireSponsorBusiness;
  const [selectedId, setSelectedId] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [business, setBusiness] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState(null);

  if (!tiers.length) return null;

  const selected = tiers.find(t => t.id === selectedId) || null;

  const choose = (tier) => {
    setError(null);
    setSelectedId(prev => (prev === tier.id ? null : tier.id));
  };

  const handlePay = async () => {
    setError(null);
    if (!selected) return;
    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (!email.trim() || !email.includes('@') || !email.includes('.')) {
      setError('Please enter a valid email address.'); return;
    }
    if (requireBusiness && !business.trim()) {
      setError('Please enter your business or organization name.'); return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/events/${event.id}/sponsor`, {
        sponsorshipTierId:   selected.id,
        sponsorName:         name.trim(),
        sponsorEmail:        email.trim(),
        sponsorBusinessName: business.trim() || null,
      });
      if (res.data?.paymentUrl) {
        setRedirecting(true);
        setTimeout(() => { window.location.href = res.data.paymentUrl; }, 400);
      } else {
        setError('Could not start payment. Please email info@njjba.com.');
        setSubmitting(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start your sponsorship. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <section style={sponsorSectionStyle}>
      <h2 style={sectionTitleStyle}>Sponsor This Event</h2>
      <div style={{ fontSize: 14, color: '#475569', margin: '-8px 0 16px' }}>
        Support the event by choosing a sponsorship level below.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {tiers.map(tier => {
          const isOpen = selectedId === tier.id;
          const price = parseFloat(tier.price ?? 0).toFixed(2);
          return (
            <div key={tier.id} style={sponsorItemStyle}>
              <button type="button" onClick={() => choose(tier)} style={sponsorButtonStyle(isOpen)}>
                <span style={{ fontWeight: 600, fontSize: 15, color: '#1f2937' }}>{tier.name}</span>
                <span style={{ fontWeight: 600, fontSize: 14, color: '#1a3a5c' }}>{isOpen ? 'Close ✕' : 'Click to learn more →'}</span>
              </button>

              {isOpen && (
                <div style={sponsorDetailStyle}>
                  <div style={{ fontSize: 26, fontWeight: 800, color: '#1a3a5c', marginBottom: 12 }}>${price}</div>
                  {tier.benefits_description && (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Benefits</div>
                      <p style={{ margin: '0 0 10px', fontSize: 14, color: '#374151', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                        {tier.benefits_description}
                      </p>
                    </>
                  )}
                  {tier.included_tickets ? (
                    <p style={{ margin: '0 0 10px', fontSize: 13, color: '#4b5563' }}>
                      Includes {tier.included_tickets} ticket{tier.included_tickets === 1 ? '' : 's'}.
                    </p>
                  ) : null}

                  <div style={formRowStyle}>
                    <div style={{ ...formGroupStyle, flex: 1, minWidth: 200 }}>
                      <label style={labelStyle}>Name *</label>
                      <input style={inputStyle} value={name} onChange={e => setName(e.target.value)}
                             placeholder="Your name or organization" />
                    </div>
                    <div style={{ ...formGroupStyle, flex: 1, minWidth: 200 }}>
                      <label style={labelStyle}>Email *</label>
                      <input style={inputStyle} type="email" value={email} onChange={e => setEmail(e.target.value)}
                             placeholder="you@example.com" />
                    </div>
                  </div>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>Business / Organization {requireBusiness ? '*' : '(optional)'}</label>
                    <input style={inputStyle} value={business} onChange={e => setBusiness(e.target.value)} />
                  </div>

                  {error && <div style={errorBoxStyle}>{error}</div>}

                  <button type="button" onClick={handlePay} disabled={submitting || redirecting} style={ctaButtonStyle}>
                    {redirecting ? 'Redirecting to payment…' : submitting ? 'Starting…' : `Pay $${price} →`}
                  </button>
                  <div style={{ ...hintStyle, marginTop: 8 }}>Payment is processed securely by Cardknox.</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

const sponsorSectionStyle = { marginTop: 28, paddingTop: 24, borderTop: '1px solid #e5e7eb' };
const sponsorItemStyle = { border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' };
const sponsorButtonStyle = (open) => ({
  width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '14px 16px', background: open ? '#f1f5f9' : '#ffffff', border: 'none',
  cursor: 'pointer', textAlign: 'left',
});
const sponsorDetailStyle = { padding: '4px 16px 16px', borderTop: '1px solid #eef2f7' };

const pageStyle = {
  minHeight:  '100vh',
  background: '#f7f8fa',
  fontFamily: '"Helvetica Neue", Arial, sans-serif',
  color:      '#1f2937',
  padding:    '24px 16px 48px',
};
const containerStyle = { maxWidth: 820, margin: '0 auto' };
const backLinkStyle = {
  display:        'inline-block',
  marginBottom:   16,
  color:          '#1a3a5c',
  textDecoration: 'none',
  fontSize:       14,
  fontWeight:     600,
};
const cardStyle = {
  background:   '#fff',
  borderRadius: 10,
  overflow:     'hidden',
  boxShadow:    '0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)',
};
const flyerWrapStyle = {
  width:      '100%',
  background: '#f3f4f6',
  display:    'flex',
  alignItems: 'center',
  justifyContent: 'center',
  maxHeight:  500,
  overflow:   'hidden',
};
const flyerImgStyle  = { width: '100%', height: 'auto', maxHeight: 500, objectFit: 'contain', display: 'block' };
const bodyStyle      = { padding: '28px 32px' };
const dateBadgeStyle = {
  display:       'inline-block',
  background:    '#1a3a5c',
  color:         '#fff',
  padding:       '4px 12px',
  borderRadius:  4,
  fontSize:      12,
  fontWeight:    600,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom:  10,
};
const titleStyle  = { fontSize: 28, fontWeight: 700, margin: '0 0 18px', color: '#1f2937', lineHeight: 1.2 };
const metaGridStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  marginBottom: 20,
  padding: '14px 16px',
  background: '#f9fafb',
  borderRadius: 6,
  fontSize: 14,
};
const metaItemStyle = { color: '#374151' };
const descriptionStyle    = { fontSize: 15, lineHeight: 1.6, color: '#374151', marginBottom: 24 };
const ticketsSectionStyle = { marginTop: 24, paddingTop: 24, borderTop: '1px solid #e5e7eb' };
const sponsorshipsSectionStyle = { marginTop: 24, paddingTop: 24, borderTop: '1px solid #e5e7eb' };
const sponsorshipsListStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
  gap: 14,
};
const sponsorshipCardStyle = {
  display: 'flex',
  flexDirection: 'column',
  padding: 20,
  background: '#fff',
  border: '2px solid #1e3a5f',
  borderRadius: 8,
  boxShadow: '0 2px 8px rgba(30, 58, 95, 0.08)',
};
const sponsorshipButtonStyle = {
  display: 'block',
  width: '100%',
  padding: '12px 16px',
  background: '#1e3a5f',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  textAlign: 'center',
  letterSpacing: 0.3,
};
const sponsorshipLearnMoreStyle = {
  display: 'block',
  width: '100%',
  padding: '12px 16px',
  background: '#fff',
  color: '#1e3a5f',
  border: '2px solid #1e3a5f',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  textAlign: 'center',
  letterSpacing: 0.3,
};
const sponsorModalOverlayStyle = {
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  zIndex: 1000,
};
const sponsorModalStyle = {
  position: 'relative',
  background: '#fff',
  borderRadius: 12,
  padding: 28,
  maxWidth: 440,
  width: '100%',
  maxHeight: '85vh',
  overflowY: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};
const sponsorModalCloseStyle = {
  position: 'absolute',
  top: 12, right: 12,
  width: 32, height: 32,
  border: 'none',
  background: '#f1f5f9',
  borderRadius: 6,
  fontSize: 20,
  lineHeight: 1,
  color: '#475569',
  cursor: 'pointer',
};
const sectionTitleStyle   = { fontSize: 20, fontWeight: 600, margin: '0 0 14px', color: '#1f2937' };
const ticketsListStyle    = { display: 'flex', flexDirection: 'column', gap: 10 };
const ticketRowStyle      = {
  display:      'flex',
  justifyContent: 'space-between',
  alignItems:   'center',
  gap:          16,
  padding:      '14px 16px',
  background:   '#f9fafb',
  borderRadius: 6,
};
const ticketNameStyle    = { fontSize: 15, fontWeight: 600, color: '#1f2937' };
const ticketDescStyle    = { fontSize: 13, color: '#4b5563', lineHeight: 1.5, marginTop: 4, fontStyle: 'italic' };
const eventMessageStyle  = {
  padding: '12px 16px',
  background: '#fef3c7',
  border: '1px solid #fde68a',
  borderRadius: 6,
  fontSize: 14,
  color: '#78350f',
  lineHeight: 1.5,
  marginBottom: 16,
  whiteSpace: 'pre-wrap',
};
const soldOutBadgeStyle  = {
  display:'inline-block', marginTop: 4, padding: '2px 8px', borderRadius: 3,
  background: '#fee2e2', color: '#991b1b', fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
};
const pricesStyle        = { display: 'flex', gap: 18 };
const priceItemStyle     = { textAlign: 'center' };
const priceLabelStyle    = { fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 };
const priceValueStyle    = { fontSize: 16, fontWeight: 700, color: '#1a3a5c' };
const registerSectionStyle = { marginTop: 28, paddingTop: 24, borderTop: '1px solid #e5e7eb' };
const registerTextStyle  = { fontSize: 14, color: '#4b5563', lineHeight: 1.6, marginBottom: 14 };
const ctaButtonStyle     = {
  display:        'inline-block',
  background:     '#1a3a5c',
  color:          '#fff',
  padding:        '10px 22px',
  borderRadius:   6,
  textDecoration: 'none',
  fontWeight:     600,
  fontSize:       14,
  border:         'none',
};
const footerStyle = { textAlign: 'center', marginTop: 32, color: '#6b7280', fontSize: 13 };
const linkStyle   = { color: '#1a3a5c', fontWeight: 600 };
const statusStyle = { textAlign: 'center', padding: 48, color: '#6b7280', fontSize: 16 };

// ─── Form styles ────────────────────────────────────────────────────────────
const formGroupStyle = { marginBottom: 14 };
const formRowStyle = { display: 'flex', gap: 12, flexWrap: 'wrap' };
const labelStyle = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 4,
};
const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: 15,
  border: '1px solid #d1d5db',
  borderRadius: 6,
  background: '#fff',
  color: '#1f2937',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};
const hintStyle = {
  fontSize: 12,
  color: '#6b7280',
  marginTop: 4,
};
const summaryBoxStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '14px 16px',
  background: '#f9fafb',
  borderRadius: 6,
  marginTop: 16,
  marginBottom: 12,
};
const qtyBtnStyle = {
  width: 40,
  height: 40,
  fontSize: 22,
  fontWeight: 700,
  lineHeight: 1,
  color: '#1a3a5c',
  background: '#fff',
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const attendeeCardStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: '14px 14px 2px',
  marginBottom: 14,
  background: '#fcfcfd',
};
const attendeeHeadingStyle = {
  fontSize: 13,
  fontWeight: 700,
  color: '#1a3a5c',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  marginBottom: 10,
};
const errorBoxStyle = {
  padding: '10px 14px',
  background: '#fef2f2',
  border: '1px solid #fecaca',
  color: '#991b1b',
  borderRadius: 6,
  fontSize: 14,
  marginBottom: 12,
};
const submitButtonStyle = {
  width: '100%',
  padding: '14px 22px',
  fontSize: 16,
  fontWeight: 600,
  marginTop: 4,
  textAlign: 'center',
};
const disclaimerStyle = {
  marginTop: 14,
  fontSize: 12,
  color: '#6b7280',
  lineHeight: 1.5,
};
const appliedPromoStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '10px 14px',
  background: '#dcfce7',
  border: '1px solid #86efac',
  borderRadius: 6,
};
const removePromoBtn = {
  background: 'none',
  border: 'none',
  fontSize: 22,
  color: '#15803d',
  cursor: 'pointer',
  padding: 0,
  marginLeft: 8,
  lineHeight: 1,
};
const promoLinkBtn = {
  background: 'none',
  border: 'none',
  color: '#1a3a5c',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  padding: 0,
  marginTop: 6,
  textDecoration: 'underline',
};

// ─── Helpers ────────────────────────────────────────────────────────────────
function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
}
function formatTime(start, end) {
  if (!start) return '';
  const fmt = (t) => {
    const [hh, mm] = t.split(':');
    const h = parseInt(hh, 10);
    const m = mm || '00';
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${m} ${period}`;
  };
  return end ? `${fmt(start)} – ${fmt(end)}` : fmt(start);
}

// ─── After registering: a clear, friendly confirmation ────────────────────────  [marker: confirm-polish v1]
const C = { blue: '#003897', ink: '#1f2937', muted: '#6b7280', line: '#e5e7eb', green: '#15803d', paleGreen: '#ecfdf3', paleBlue: '#eef3fb' };
export function RegistrationConfirmed({ event, status, free, count, email, names, paymentUrl }) {
  const place = [event?.venue, event?.location_address].filter(Boolean).join(', ');
  const maps = place ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place)}` : null;
  const n = count || (names ? names.length : 1);
  const paid = status === 'paid';
  const head = paid ? (free ? (n > 1 ? `All ${n} tickets are confirmed` : "You're registered") : "Payment received. You're registered")
    : status === 'pending' ? 'Confirming your payment…' : 'Thank you';
  const box = { background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, padding: '18px 20px', marginTop: 14 };
  const row = { display: 'flex', gap: 12, alignItems: 'flex-start', padding: '8px 0' };
  const lab = { minWidth: 64, fontSize: 13, color: C.muted, paddingTop: 2 };
  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: '8px 0 24px', textAlign: 'left' }} role="status">
      <div style={{ background: paid ? C.paleGreen : C.paleBlue, border: `1px solid ${paid ? '#b7e4c7' : '#c9d7f2'}`, borderRadius: 12, padding: '22px 20px', textAlign: 'center' }}>
        <div aria-hidden="true" style={{ width: 52, height: 52, borderRadius: '50%', margin: '0 auto 10px', display: 'grid', placeItems: 'center',
          background: paid ? C.green : C.blue, color: '#fff', fontSize: 26, fontWeight: 700 }}>{paid ? '✓' : '…'}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: paid ? C.green : C.blue }}>{head}</div>
        {event && <div style={{ fontSize: 15, color: C.ink, marginTop: 6 }}>{event.name}</div>}
      </div>

      {event && (
        <div style={box}>
          <div style={row}><span style={lab}>When</span>
            <span style={{ color: C.ink }}><strong>{formatDate(event.event_date)}</strong>{event.start_time ? <><br />{formatTime(event.start_time, event.end_time)}</> : null}</span></div>
          {place && <div style={row}><span style={lab}>Where</span>
            <span style={{ color: C.ink }}>{event.venue && <strong>{event.venue}</strong>}{event.venue && event.location_address ? <br /> : null}{event.location_address}
              {maps && <><br /><a href={maps} target="_blank" rel="noopener noreferrer" style={{ color: C.blue, fontWeight: 600 }}>Get directions</a></>}</span></div>}
          {names && names.filter(Boolean).length > 0 && <div style={row}><span style={lab}>{names.length > 1 ? 'Tickets' : 'Ticket'}</span>
            <span style={{ color: C.ink }}>{names.filter(Boolean).map((x, i) => <div key={i}>{x}</div>)}</span></div>}
          <div style={{ marginTop: 10 }}><AddToCalendarButton event={event} /></div>
        </div>
      )}

      <div style={box}>
        <div style={{ fontWeight: 600, color: C.ink, marginBottom: 6 }}>What happens next</div>
        <ul style={{ margin: 0, paddingLeft: 18, color: C.ink, fontSize: 14, lineHeight: 1.7 }}>
          {status === 'pending' ? <>
            <li>We're waiting for the payment to come through. This usually takes less than a minute; this page updates on its own.</li>
            <li>Once it does, your confirmation email {email ? <>goes to <strong>{email}</strong></> : 'is sent'}.</li>
          </> : <>
            <li>A confirmation email {email ? <>is on its way to <strong>{email}</strong></> : 'is on its way'}{n > 1 ? ', and each guest with an email address gets their own' : ''}.</li>
            <li>At check-in, show the QR code from that email, or just give your name.</li>
            <li>Didn't get the email in 10 minutes? Check your spam folder, or contact us.</li>
          </>}
        </ul>
        {status === 'pending' && paymentUrl && (
          <p style={{ fontSize: 13, color: C.muted, margin: '10px 0 0' }}>Didn't finish paying? <a href={paymentUrl} style={{ color: C.blue, fontWeight: 600 }}>Go back to the payment page</a>.</p>
        )}
      </div>

      <p style={{ fontSize: 13, color: C.muted, textAlign: 'center', marginTop: 16 }}>
        Questions? Call 201-472-5120 or email <a href="mailto:info@njjba.com" style={{ color: C.blue, fontWeight: 600 }}>info@njjba.com</a>.
        {' '}<a href="https://www.njjba.com/events" style={{ color: C.blue }}>See all upcoming events</a>
      </p>
    </div>
  );
}

// /thank-you — where people land after paying (set this as the PaymentSITE's "after payment" address)
export function ThankYouPage() {
  const qs = new URLSearchParams(window.location.search);
  const [order] = useState(() => {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem('njjba-last-order') || 'null'); } catch (_) { /* ignore */ }
    if (saved && Date.now() - saved.at > 3 * 24 * 3600 * 1000) saved = null;
    const ref = qs.get('order') || qs.get('xInvoice') || qs.get('invoice') || (saved && saved.orderRef) || null;
    return { ref, saved: saved && (!ref || saved.orderRef === ref) ? saved : null };
  });
  const [status, setStatus] = useState(order.ref ? 'pending' : 'unknown');
  const [event, setEvent] = useState(null);
  const [eventId, setEventId] = useState(order.saved ? order.saved.eventId : null);

  useEffect(() => {
    if (!order.ref) return undefined;
    let tries = 0; let stop = false; let timer = null;
    const poll = () => {
      api.get(`/orders/${encodeURIComponent(order.ref)}/status`).then(r => {
        if (stop) return;
        if (r.data.event_id) setEventId(id => id || r.data.event_id);
        setStatus(r.data.status === 'paid' ? 'paid' : r.data.status === 'pending' ? 'pending' : 'unknown');
        if (r.data.status === 'pending' && ++tries < 40) timer = setTimeout(poll, 4000);
      }).catch(() => { if (!stop) setStatus('unknown'); });
    };
    poll();
    return () => { stop = true; clearTimeout(timer); };
  }, [order.ref]);
  useEffect(() => { if (eventId) api.get(`/events/${eventId}`).then(r => setEvent(r.data)).catch(() => {}); }, [eventId]);

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fb', padding: '24px 16px' }}>
      <div style={{ maxWidth: 560, margin: '0 auto 12px', fontWeight: 700, color: C.blue }}>New Jersey Jewish Business Alliance</div>
      <RegistrationConfirmed event={event} status={status} email={order.saved && order.saved.email}
        names={order.saved && order.saved.names} paymentUrl={order.saved && order.saved.paymentUrl} />
    </div>
  );
}

// [marker: website-feed v1] inside the website (njjba.com) the back link goes to the website's Events page
function BackToEvents() {
  const onWebsite = window.self === window.top && /(^|\.)njjba\.com$/i.test(window.location.hostname) && !/^portal\./i.test(window.location.hostname);
  return onWebsite
    ? <a href="/events" style={backLinkStyle}>← Back to events</a>
    : <Link to="/embed/events" style={backLinkStyle}>← Back to events</Link>;
}
